import { ConfirmModal, Toasts, useModalContext } from '@fourthwall/components'
import { routing } from '@utils/routing'

import logoSrc from '../../../assets/signets/vimmi.svg'
import type { ConnectBarProps } from '../../../components/ConnectBar/ConnectBar'
import { useAppConnectEvent } from '../../../hooks/useAppConnectEvent'
import { useAppConnectedEvent } from '../../../hooks/useAppConnectedEvent'
import { useDisconnectPlatformAppMutation } from '../../../queries/useDisconnectPlatformAppMutation'
import { usePlatformAppQuery } from '../../../queries/usePlatformAppQuery'

export const useVimmi = () => {
  const {
    platformAppQuery: { data: vimmiApp, refetch, isLoading },
  } = usePlatformAppQuery([{ appId: 'apps.vimmi' }])

  const { open } = useModalContext()
  const appConnectEvent = useAppConnectEvent('vimmi')
  const { disconnectPlatformAppMutation: disconnectVimmiMutation } =
    useDisconnectPlatformAppMutation('apps.vimmi')

  useAppConnectedEvent('vimmi')

  const getStatus = (): 'CONNECTED' | 'NOT_CONNECTED' | undefined => {
    if (!vimmiApp) return undefined

    if (vimmiApp.state === 'UNINSTALLED' || vimmiApp.state === 'NEEDS_RECONNECT')
      return 'NOT_CONNECTED'

    if (vimmiApp.state === 'INSTALLED') return 'CONNECTED'
  }

  const status = getStatus()

  const getConnectBar = (): ConnectBarProps | undefined => {
    const connectBarBase = {
      logoSrc,
      description: status === 'CONNECTED' ? 'Connected to Vimmi' : 'Connect your Vimmi account',
      disabled: false,
    }

    if (!status) return undefined

    if (status === 'NOT_CONNECTED') {
      const onConnect = () => {
        appConnectEvent.trigger()

        window.location.assign(routing.external.vimmi.connect)
      }

      return {
        ...connectBarBase,
        onConnect,
      }
    }

    if (status === 'CONNECTED') {
      const onDisconnect = () => {
        open(ConfirmModal, {
          title: 'Are you sure you want to disconnect from Vimmi?',
          confirmLabel: 'Yes, disconnect',
          confirmAppearance: 'destructive',
          onConfirm: () => {
            const toastId = 'disconnectVimmiMutation'

            Toasts.notify('Disconnecting from Vimmi...', {
              toastId,
              type: 'info',
              autoClose: false,
            })

            disconnectVimmiMutation.mutate(undefined, {
              onSuccess: () => {
                refetch()
                Toasts.notify('Disconnected from Vimmi successfully.', { type: 'success' })
                Toasts.dismiss(toastId)
              },
            })
          },
        })
      }

      return {
        ...connectBarBase,
        disabled: disconnectVimmiMutation.isLoading,
        onDisconnect,
      }
    }
  }

  return {
    connectBar: getConnectBar(),
    hmac:
      vimmiApp?.hmac && vimmiApp?.hmacTimestamp
        ? {
            signature: vimmiApp.hmac,
            timestamp: vimmiApp.hmacTimestamp,
          }
        : undefined,
    status,
    isLoading,
  }
}
